import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { get } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/** Address autocomplete backed by Google Places (via backend). onSelect({address, place_id}) */
export const AddressInput = ({ value, onChange, onSelect, placeholder = "Søk adresse…", publicMode = false, testId = "address-input", className, disabled }) => {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  const box = useRef();

  useEffect(() => setQ(value || ""), [value]);

  useEffect(() => {
    const h = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const search = (text) => {
    clearTimeout(timer.current);
    if (!text || text.length < 3) {
      setItems([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await get(publicMode ? "/public/maps/autocomplete" : "/maps/autocomplete", { q: text });
        setItems(res || []);
        setOpen(true);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 280);
  };

  return (
    <div className={cn("relative", className)} ref={box}>
      <div className="relative">
        <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid={testId}
          value={q}
          disabled={disabled}
          placeholder={placeholder}
          className="pl-8"
          onChange={(e) => {
            setQ(e.target.value);
            onChange?.(e.target.value);
            search(e.target.value);
          }}
          onFocus={() => items.length && setOpen(true)}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {open && items.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-64 overflow-auto" role="listbox">
          {items.map((it) => (
            <li
              key={it.place_id}
              data-testid="address-suggestion"
              role="option"
              className="cursor-pointer px-3 py-2 text-sm hover:bg-muted"
              onMouseDown={(e) => {
                e.preventDefault();
                setQ(it.description);
                onChange?.(it.description);
                onSelect?.({ address: it.description, place_id: it.place_id });
                setOpen(false);
              }}
            >
              <div className="font-medium">{it.main_text || it.description}</div>
              {it.secondary_text && <div className="text-xs text-muted-foreground">{it.secondary_text}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
